import React from 'react';
import { Link } from 'react-router-dom';
import { QuizApiClient } from '@/infrastructure/quiz/QuizApiClient';
import { PLACEHOLDER_THUMB, SPOTLIGHT_LIMIT, SPOTLIGHT_SUBJECT, getQuizTypeUi } from '@/presentation/quiz/publicQuizUiResources';
import { useDashboardLocaleStore } from '@/presentation/dashboard/useDashboardLocaleStore';
import { getPublicSpotlightStrings } from '@/presentation/dashboard/dashboardHomeLocale';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { quizDifficultyLabel } from '@/i18n/quizDifficulty';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

export default function PublicIeltsQuizSpotlight() {
  const { t } = useAppTranslation();
  const locale = useDashboardLocaleStore((s) => s.locale);
  const spot = React.useMemo(() => getPublicSpotlightStrings(locale), [locale]);
  const api = React.useMemo(() => new QuizApiClient(), []);
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState([]);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .listPublicQuizzes({ subject: SPOTLIGHT_SUBJECT, limit: SPOTLIGHT_LIMIT })
      .then((data) => {
        if (!mounted) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(formatApiErrorMessage(e?.message, t) || t('errors.loadPublicQuizzes'));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [api, locale, t]);

  return (
    <section className="mx-auto w-full max-w-7xl rounded-3xl border border-cyan-100 bg-white p-5 shadow-sm">
      {loading ? <p className="text-center text-sm text-slate-500">{spot.loading}</p> : null}
      {!loading && error ? <p className="text-center text-sm font-semibold text-rose-700">{error}</p> : null}
      {!loading && !error && items.length === 0 ? (
        <p className="text-center text-sm text-slate-500">{spot.empty}</p>
      ) : null}
      <div className="overflow-x-auto">
        <div className="grid min-w-max grid-flow-col auto-cols-[250px] gap-3">
          {items.map((q) => {
            const meta = getQuizTypeUi(q.quiz_type);
            return (
              <Link
                key={q.id}
                to={`/public-quizzes/${q.share_code}`}
                className="grid aspect-[1/1.06] grid-rows-[auto_auto_1fr_auto] rounded-2xl border border-slate-100 bg-slate-50 p-3 transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-flex items-center rounded-full p-1.5 ${meta.tone}`} title={meta.label}>
                    <span className="material-symbols-outlined !text-sm">{meta.icon}</span>
                  </span>
                  <div className="flex items-center gap-1 text-[11px]">
                    <span className="rounded-full bg-slate-200 px-2 py-1 font-bold text-slate-700">
                      {q.estimated_time ? `${q.estimated_time}p` : '--'}
                    </span>
                    <span className="rounded-full bg-cyan-100 px-2 py-1 font-bold text-cyan-700">
                      {quizDifficultyLabel(t, q.difficulty)}
                    </span>
                  </div>
                </div>
                <h3 className="mt-2 line-clamp-2 font-bold text-slate-900">{q.title}</h3>
                <p className="mt-1 line-clamp-2 text-xs text-slate-600">
                  {q.description || spot.descFallback}
                </p>
                <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <img
                    src={PLACEHOLDER_THUMB}
                    alt={spot.thumbAlt}
                    className="aspect-square w-full object-cover opacity-80"
                  />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
